import React, { FC, forwardRef, ReactNode, useMemo, useState } from "react";

import { authTokensAtom, MediaField, MediaType, Position, Tag, useShare } from "@keepeek/commons";
import {
  AudioViewer,
  BaseComponentProps,
  getTagsDataByPosition,
  getVisualAlertsDataByPosition,
  PDFViewer,
  PictureViewer,
  PictureViewerAction,
  PictureViewerProps,
  Player3DViewer,
  PlayerViewer,
  PlayerViewerProps,
  Tags,
  useThemeColors,
} from "@keepeek/refront-components";
import { KTheme } from "@keepeek/refront-customers";
import { Box, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import cloneDeep from "lodash/cloneDeep";
import { useRouter } from "next/router";
import "react-h5-audio-player/lib/styles.css";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { Tags as TagsAdmin } from "../../models/configuration/components/tags";
import { Viewer } from "../../models/configuration/components/viewer";
import { Element } from "../../models/configuration/pages/element";
import { useConfiguration } from "../../providers/config/hooks";
import { configSectionPageElementSelector } from "../../providers/config/selectors";
import { visualAlertsAdaptor } from "../../providers/config/utils";
import { pdfViewerFullScreenState } from "../../providers/viewer/atoms";
import { playerViewerSelector } from "../../providers/viewer/selectors";

export type KViewerProps = {} & PictureViewerProps & BaseComponentProps;

const PREFIX = "KViewer";

const classes = {
  root: `${PREFIX}-root`,
  visualAlertsTopRight: `${PREFIX}-visualAlertsTopRight`,
  visualAlertsBottomRight: `${PREFIX}-visualAlertsBottomRight`,
  visualAlertsBottomLeft: `${PREFIX}-visualAlertsBottomLeft`,
  visualAlertsTopLeft: `${PREFIX}-visualAlertsTopLeft`,
};

const StyledKViewer = styled(Box)<{ mediaType: MediaType }>(({ theme, mediaType }) => ({
  [`&.${classes.root}`]: {
    position: "relative",
  },
  [`& .${classes.visualAlertsTopLeft}`]: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  [`& .${classes.visualAlertsTopRight}`]: {
    position: "absolute",
    // to prevent the visual alert to be on top of the pdf player actions
    top: mediaType === MediaType.DOCUMENT ? theme.spacing(7.5) : 0,
    right: 0,
  },
  [`& .${classes.visualAlertsBottomLeft}`]: {
    position: "absolute",
    // to prevent the visual alert to be on top of the player actions
    bottom: needPlayer(mediaType) ? theme.spacing(7.5) : 0,
    left: 0,
  },
  [`& .${classes.visualAlertsBottomRight}`]: {
    position: "absolute",
    // to prevent the visual alert to be on top of the player actions
    bottom: needPlayer(mediaType) ? theme.spacing(7.5) : 0,
    right: 0,
  },
}));

export function needPlayer(mediaType: MediaType): boolean {
  // for the moment, only video type is supported (not audio => need to adapt BO API)
  return (mediaType as MediaType) === MediaType.VIDEO;
}

const KViewer = forwardRef<HTMLDivElement, KViewerProps>((props, ref) => {
  const { t } = useTranslation();
  const theme = useTheme<KTheme>();
  const { background, hideCover } = useConfiguration<Viewer>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_VIEWER,
  ) ?? {
    background: undefined,
  };
  const { activateRMM } = useConfiguration<Element>(CONFIGURATION_SECTION_PATH.PAGES_ELEMENT) ?? {
    activateRMM: undefined,
  };

  const mediaType = props.media.formType || MediaType.OTHER;

  enum Player3DAvailableExtension {
    USDZ = "usdz",
    GLB = "glb",
  }

  const isPlayerNeeded: boolean = useMemo(
    () => mediaType !== undefined && needPlayer(mediaType),
    [mediaType],
  );
  const playerViewerPropsLoadable = useRecoilValue<PlayerViewerProps | null>(
    playerViewerSelector(isPlayerNeeded ? props.media.id : null),
  );
  const { convertColor } = useThemeColors();
  const router = useRouter();
  const { visualAlerts: visualAlertsOnElementsConf } = useRecoilValue(
    configSectionPageElementSelector,
  ) ?? { visualAlerts: undefined };
  const tagsConfiguration: TagsAdmin | undefined = useConfiguration(
    CONFIGURATION_SECTION_PATH.COMPONENTS_TAGS,
  );
  const isTagEnabled: boolean =
    tagsConfiguration !== null &&
    tagsConfiguration?.tagsToDisplayId !== null &&
    tagsConfiguration?.tagsToDisplayId?.length !== 0;

  const visualAlertsDataByPosition: Map<Position, Tag[]> | undefined = useMemo(
    () =>
      isTagEnabled
        ? undefined
        : getVisualAlertsDataByPosition(
            visualAlertsAdaptor(visualAlertsOnElementsConf),
            props?.media,
            router.locale,
          ),
    [isTagEnabled, visualAlertsOnElementsConf, props?.media, router.locale],
  );

  const tagsDataByPosition: Map<Position, Tag[]> | undefined = useMemo(
    () =>
      isTagEnabled
        ? getTagsDataByPosition(
            tagsConfiguration,
            props?.media.labels,
            // In the detail page we want to display all the tags, so there is no "maxTagsToDisplay"
            undefined,
          )
        : undefined,
    [isTagEnabled, tagsConfiguration, props?.media],
  );

  const tagsToDisplay = visualAlertsDataByPosition || tagsDataByPosition;

  const backgroundMain = convertColor(background?.main);
  const backgroundTextContrast = convertColor(background?.contrastText);

  const pictureViewer = useMemo(() => {
    // Optim to avoid flash of the preview picture then the player video
    // Just show the player video directly instead
    if (isPlayerNeeded) {
      return null;
    }
    return (
      <PictureViewer
        {...props}
        topRightZoomIn={
          <PictureViewerAction
            title={t("refront-components:components.picture-viewer.action.zoom-in.label")}
            ariaLabel={t("refront-components:components.picture-viewer.action.zoom-in.label")}
            icon={<KIcon className="fa-search-plus" />}
            sx={{
              padding: "6px 6px 4px 6px",
            }}
          />
        }
        topRightZoomOut={
          <PictureViewerAction
            title={t("refront-components:components.picture-viewer.action.zoom-out.label")}
            ariaLabel={t("refront-components:components.picture-viewer.action.zoom-out.label")}
            icon={<KIcon className="fa-search-minus" />}
            sx={{
              padding: "6px 6px 4px 6px",
            }}
          />
        }
        className={clsx("KViewer", props.className)}
        style={{
          color:
            backgroundTextContrast ||
            theme.palette.kViewer?.background?.contrastText ||
            theme.palette.text.primary,
          backgroundColor:
            backgroundMain ||
            theme.palette.kViewer?.background?.main ||
            theme.palette.background.default,
        }}
      />
    );
  }, [
    backgroundMain,
    backgroundTextContrast,
    isPlayerNeeded,
    props,
    t,
    theme.palette.background.default,
    theme.palette.kViewer?.background?.contrastText,
    theme.palette.kViewer?.background?.main,
    theme.palette.text.primary,
  ]);

  const playerViewer: ReactNode = useMemo(() => {
    if (playerViewerPropsLoadable) {
      // need to deep clone recoil value because in playerviewer, some values are modified (like resolution array) and recoil
      // do not allow to change state
      const clonedProps = cloneDeep(playerViewerPropsLoadable);

      return <PlayerViewer key={props.media.id} {...clonedProps} />;
    }
    return null;
  }, [playerViewerPropsLoadable, props.media.id]);

  const setIsFullScreen = useSetRecoilState(pdfViewerFullScreenState);
  const [pdfError, setPdfError] = useState(false);
  const pdfViewer: ReactNode = useMemo(() => {
    if (props.media.previewPdfUrl && !pdfError) {
      return (
        <PDFViewer
          file={props.media.previewPdfUrl}
          preview={{
            url: props.media.previewUrl,
            alt:
              typeof props.media.title.value === "string"
                ? props.media.title.value
                : props.media.title.value.join(" "),
          }}
          onLoadError={() => setPdfError(true)}
          onFullScreen={setIsFullScreen}
          KIcon={KIcon}
        />
      );
    }
    return null;
  }, [
    pdfError,
    props.media.previewPdfUrl,
    props.media.previewUrl,
    props.media.title.value,
    setIsFullScreen,
  ]);

  const audioViewer: ReactNode = useMemo(() => {
    if (mediaType === MediaType.AUDIO) {
      return (
        <Box
          className={clsx("audioViewerContainer")}
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            className={clsx("audioViewerInternalContainer")}
            sx={{
              backgroundColor: (theme) => theme.palette.common.white,
              width: "100%",
              padding: (theme) => theme.spacing(2),
              margin: (theme) => theme.spacing(2),
              borderRadius: "4px",
              border: (theme) => `4px solid ${theme.palette.divider}`,
              // we don't want the viewer too big for the accessibility reason
              maxWidth: "1000px",
            }}
          >
            <AudioViewer
              className={"audioViewer"}
              file={props?.media?.previewAudioUrl || ""}
              coverImage={props?.media?.previewUrl}
              displayLoopButton
              coverImageMaxHeight={20}
              coverImageCover={false}
              hideCover={hideCover}
              KIcon={KIcon}
            />
          </Box>
        </Box>
      );
    }
    return null;
  }, [mediaType, props?.media?.previewAudioUrl, props?.media?.previewUrl, hideCover]);

  const { tokenId, isShare } = useShare(router.pathname, router.query.tokenId);
  const token = useRecoilValue(authTokensAtom);
  const rmmViewer: ReactNode = useMemo(() => {
    if (mediaType === MediaType.RICH_MEDIA && activateRMM) {
      let tokenParam: string;
      if (isShare && tokenId) {
        tokenParam = `tokenId=${tokenId}`;
      } else {
        tokenParam = `token=${token}`;
      }
      return (
        <Box
          className={clsx("rmmViewerContainer")}
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            className={clsx("rmmViewerInternalContainer")}
            sx={{
              backgroundColor: (theme) => theme.palette.common.white,
              width: "100%",
              height: "100%",
              padding: (theme) => theme.spacing(2),
            }}
          >
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              src={`${location.origin}/player/rmm?id=${props.media.id}&${tokenParam}`}
            />
          </Box>
        </Box>
      );
    }
    return null;
  }, [activateRMM, isShare, mediaType, props.media.id, token, tokenId]);

  const player3DViewer: ReactNode = useMemo(() => {
    const fileExtension: string =
      (props.media.fields?.find((f) => f.id === MediaField.EXTENSION)?.value as string) || "";
    if (
      mediaType === MediaType.OTHER &&
      props.media.hiresUrl &&
      (Object.values(Player3DAvailableExtension) as string[]).includes(fileExtension)
    ) {
      return <Player3DViewer src={props.media.hiresUrl} id={`media-${props.media.id}`} />;
    }
    return null;
  }, [
    Player3DAvailableExtension,
    mediaType,
    props.media.fields,
    props.media.hiresUrl,
    props.media.id,
  ]);

  return (
    <StyledKViewer
      ref={ref}
      className={clsx("KViewer", classes.root, props.className)}
      mediaType={mediaType}
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {pdfViewer || playerViewer || audioViewer || rmmViewer || player3DViewer || pictureViewer}

      {/* Visual alerts or Tags on each configured corners of the player */}
      {tagsToDisplay &&
        [...tagsToDisplay.keys()].map((currentPosition) => {
          if (tagsToDisplay.has(currentPosition)) {
            return (
              <TagInPlayer
                key={currentPosition?.toString()}
                position={currentPosition}
                tagsData={tagsToDisplay.get(currentPosition) || []}
              />
            );
          }
        })}
    </StyledKViewer>
  );
});

type TagInPlayerProps = {
  tagsData: Tag[];
  position: Position;
};

const TagInPlayer: FC<React.PropsWithChildren<TagInPlayerProps>> = function ({
  tagsData,
  position,
}) {
  const computeTagClass = (position: Position): string => {
    let tagClass: string = "";
    // compute the adequate class to position the tag in the correct corner
    switch (position) {
      case Position.TopLeft:
        tagClass = classes.visualAlertsTopLeft;
        break;
      case Position.TopRight:
        tagClass = classes.visualAlertsTopRight;
        break;
      case Position.BottomLeft:
        tagClass = classes.visualAlertsBottomLeft;
        break;
      case Position.BottomRight:
        tagClass = classes.visualAlertsBottomRight;
        break;
    }
    return tagClass;
  };

  return (
    <Tags
      key={position?.toString()}
      className={computeTagClass(position)}
      tagsPosition={position}
      tagsData={tagsData || []}
    />
  );
};

export default KViewer;
